import type { SiteTemplateDefinition, TemplateRenderProps } from "../types";
import { renderRestaurantTemplate, restaurantModernTemplate } from "./restaurantModern";

function CafeStyles() {
    return (
        <style>{`
            [data-web-template="restaurant-cafe-brunch"] {
                --cafe-coffee: var(--site-text);
                --cafe-forest: var(--site-primary);
                --cafe-paper: var(--site-background);
                --cafe-sheet: var(--site-surface);
                --cafe-copper: var(--site-accent);
                --cafe-copper-ink: color-mix(in srgb, var(--site-accent) 48%, var(--site-text));
                --cafe-rule: color-mix(in srgb, var(--site-text) 22%, transparent);
                background:
                    repeating-linear-gradient(90deg, transparent 0, transparent calc(25% - 1px), color-mix(in srgb, var(--site-text) 3%, transparent) 25%),
                    var(--site-background);
                color: var(--site-text);
            }

            [data-web-template="restaurant-cafe-brunch"] .site-builder-section {
                padding-block: clamp(5rem, 9vw, 9rem);
            }

            [data-web-template="restaurant-cafe-brunch"] :where(.site-builder-card, .site-builder-image, img) {
                border-radius: 2px !important;
            }

            [data-web-template="restaurant-cafe-brunch"] .site-builder-card {
                box-shadow: none !important;
            }

            [data-web-template="restaurant-cafe-brunch"] .site-builder-card:hover,
            [data-web-template="restaurant-cafe-brunch"] .site-builder-card:focus-within {
                box-shadow: none !important;
                transform: none !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where(.site-builder-button, button:not(.restaurant-mobile-menu-toggle)) {
                border-radius: 2px !important;
                box-shadow: none !important;
                letter-spacing: .12em;
                text-transform: uppercase;
            }

            [data-web-template="restaurant-cafe-brunch"] :where(input, textarea, select) {
                background: transparent !important;
                border: 0 !important;
                border-bottom: 1px solid var(--cafe-rule) !important;
                border-radius: 0 !important;
                box-shadow: none !important;
            }

            /* A broad, asymmetric masthead instead of the shared floating bar. */
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] {
                backdrop-filter: none !important;
                background: var(--cafe-paper) !important;
                border-bottom: 1px solid var(--cafe-rule) !important;
                box-shadow: none !important;
                min-height: 142px !important;
                padding: 0 1.5rem !important;
                position: relative !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] > .site-builder-grid {
                align-items: stretch !important;
                min-height: 142px;
                width: min(100%, 1440px) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item {
                align-self: stretch;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                grid-column: 4 / -1 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                grid-column: 10 / -1 !important;
                position: relative;
                z-index: 2;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="brand"] {
                align-content: center;
                border-right: 1px solid var(--cafe-rule);
                gap: 1rem !important;
                height: 100%;
                padding-right: clamp(1rem, 3vw, 3rem);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="brand"] > .site-builder-badge {
                background: var(--cafe-forest) !important;
                border: 0 !important;
                border-radius: 50% !important;
                box-shadow: none !important;
                color: var(--cafe-sheet) !important;
                height: 2.4rem;
                padding: 0 !important;
                width: 2.4rem;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="brandCopy"] {
                gap: .2rem !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="brandCopy"] .site-builder-text > span {
                color: var(--cafe-coffee) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="brandCopy"] > [data-site-builder-block="text"]:first-child .site-builder-text {
                font-family: var(--site-heading-font) !important;
                font-size: clamp(1.7rem, 2.4vw, 2.4rem) !important;
                font-style: italic;
                font-weight: 500 !important;
                letter-spacing: -.03em !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="brandCopy"] > [data-site-builder-block="text"]:last-child .site-builder-text {
                color: var(--cafe-forest) !important;
                font-size: .64rem !important;
                letter-spacing: .24em !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="headerNavigation"] {
                align-content: center;
                align-items: center;
                align-self: end;
                background: transparent !important;
                border: 0 !important;
                border-top: 1px solid var(--cafe-rule) !important;
                border-radius: 0 !important;
                gap: clamp(1rem, 2.4vw, 2.5rem) !important;
                justify-content: flex-start !important;
                margin: 0 clamp(1rem, 2.8vw, 3rem);
                min-height: 52px;
                padding: 0 !important;
                width: auto !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text {
                font-family: var(--site-body-font) !important;
                font-size: .68rem !important;
                font-weight: 750 !important;
                letter-spacing: .14em !important;
                padding: 0 !important;
                text-transform: uppercase;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text > span,
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] :where(.site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: var(--cafe-coffee) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text::after {
                background: var(--cafe-copper) !important;
                border-radius: 0 !important;
                bottom: -.8rem !important;
                height: 3px !important;
                left: 0 !important;
                transform: scaleX(0) !important;
                transform-origin: left !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="headerNavigation"] .site-builder-text:hover::after {
                transform: scaleX(1) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="headerActions"] {
                align-content: center;
                border-left: 1px solid var(--cafe-rule);
                height: 100%;
                padding-left: clamp(1rem, 2vw, 2rem);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="headerCta"] {
                background: var(--cafe-forest) !important;
                border-color: var(--cafe-forest) !important;
                color: var(--cafe-sheet) !important;
                min-height: 48px !important;
            }

            /* Paper-and-photograph cover. */
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="hero"] {
                background-color: var(--cafe-paper) !important;
                background-position: right center !important;
                background-size: 55% 100% !important;
                border-bottom: 1px solid var(--cafe-rule);
                min-height: min(820px, calc(100svh - 142px)) !important;
                overflow: hidden;
                padding-block: clamp(5rem, 8vw, 8rem) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="hero"]::before {
                background:
                    linear-gradient(90deg, var(--cafe-paper) 0 41%, color-mix(in srgb, var(--cafe-paper) 95%, transparent) 51%, transparent 70%),
                    linear-gradient(0deg, color-mix(in srgb, var(--cafe-coffee) 20%, transparent), transparent 42%) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="hero"]::after {
                border-left: 1px solid var(--cafe-rule);
                content: "";
                inset: 0 auto 0 25%;
                pointer-events: none;
                position: absolute;
                width: 25%;
                z-index: 0;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="hero"] > .site-builder-grid {
                align-items: center !important;
                min-height: 0 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="hero"] [data-document-role="heroCopy"] {
                align-content: center;
                max-width: 42rem;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="hero"] :where(.site-builder-text, .site-builder-text > span) {
                color: var(--cafe-coffee) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroHeading"] {
                color: var(--cafe-coffee) !important;
                font-family: var(--site-heading-font) !important;
                font-size: clamp(4.5rem, 8vw, 8.5rem) !important;
                font-style: italic;
                font-weight: 400 !important;
                letter-spacing: -.065em !important;
                line-height: .78 !important;
                margin-left: -.06em;
                max-width: 700px !important;
                text-wrap: balance;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroDescription"] {
                border-top: 1px solid var(--cafe-rule);
                color: var(--site-muted) !important;
                font-size: clamp(1rem, 1.5vw, 1.22rem) !important;
                line-height: 1.65 !important;
                margin-top: .75rem;
                max-width: 31rem;
                padding-top: 1.25rem;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroPrimaryAction"],
            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroSecondaryAction"] {
                min-height: 50px !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroPrimaryAction"] {
                background: var(--cafe-copper) !important;
                border-color: var(--cafe-copper) !important;
                color: var(--cafe-sheet) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroSecondaryAction"] {
                background: transparent !important;
                border-color: var(--cafe-coffee) !important;
                color: var(--cafe-coffee) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where(
                [data-document-role="heroHighlightOne"],
                [data-document-role="heroHighlightTwo"],
                [data-document-role="heroHighlightThree"]
            ) {
                background: transparent !important;
                border: 0 !important;
                border-radius: 0 !important;
                border-top: 1px solid var(--cafe-rule) !important;
                box-shadow: none !important;
                color: var(--cafe-forest) !important;
                font-size: .62rem !important;
                letter-spacing: .13em !important;
                padding: .8rem 0 0 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where(
                [data-document-role="heroHighlightOne"],
                [data-document-role="heroHighlightTwo"],
                [data-document-role="heroHighlightThree"]
            ) .site-builder-icon {
                color: var(--cafe-copper) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroFeature"] {
                background: var(--cafe-sheet) !important;
                border: 9px solid var(--cafe-sheet) !important;
                border-bottom-width: 48px !important;
                border-radius: 1px !important;
                box-shadow: 0 30px 72px color-mix(in srgb, var(--cafe-coffee) 26%, transparent) !important;
                max-width: 350px;
                transform: rotate(1.8deg) translate(1.8rem, 2.5rem);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroFeature"] > [data-site-builder-block="image"] {
                filter: sepia(.08) saturate(.9);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroOpeningHours"] {
                gap: 0 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroOpeningHours"] > .site-builder-column {
                background: transparent !important;
                border-radius: 0 !important;
                border-top: 1px solid var(--cafe-rule);
                padding: .65rem 0 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroOpeningHours"] .site-builder-text,
            [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroOpeningHours"] .site-builder-text > span {
                color: var(--cafe-coffee) !important;
                font-size: .65rem !important;
                letter-spacing: .08em;
                text-transform: uppercase;
            }

            /* Gallery is composed like a printed photo spread. */
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] {
                background:
                    linear-gradient(90deg, transparent calc(50% - .5px), var(--cafe-rule) 50%, transparent calc(50% + .5px)),
                    color-mix(in srgb, var(--site-accent) 10%, var(--cafe-paper)) !important;
                overflow: hidden;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid {
                gap: clamp(1rem, 2vw, 2rem) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:first-child {
                border-left: 4px solid var(--cafe-copper);
                justify-self: start;
                padding-left: clamp(1.25rem, 3vw, 3rem);
                width: 82%;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-column {
                align-items: start !important;
                text-align: left !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-text {
                text-align: left !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) .site-builder-image {
                border: 11px solid var(--cafe-sheet);
                box-shadow: 0 24px 54px color-mix(in srgb, var(--cafe-coffee) 17%, transparent);
                transform: rotate(-1.2deg);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:nth-child(3) .site-builder-image {
                border: 8px solid var(--cafe-sheet);
                border-bottom-width: 28px;
                box-shadow: 0 18px 38px color-mix(in srgb, var(--cafe-coffee) 15%, transparent);
                transform: rotate(2.2deg) translateY(1.2rem);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:nth-child(4) .site-builder-image {
                border: 8px solid var(--cafe-sheet);
                border-bottom-width: 28px;
                box-shadow: 0 18px 38px color-mix(in srgb, var(--cafe-coffee) 15%, transparent);
                transform: rotate(-1.8deg) translateY(-.4rem);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:nth-child(5) > .site-builder-column {
                background: var(--cafe-sheet) !important;
                border: 1px solid var(--cafe-rule) !important;
                border-radius: 0 !important;
                box-shadow: 12px 12px 0 color-mix(in srgb, var(--cafe-copper) 16%, transparent);
                padding: clamp(1.5rem, 3vw, 3rem) !important;
            }

            /* Menu reads as a ruled roastery price sheet, never as cards. */
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="menu"],
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="fullMenu"] {
                background: var(--cafe-paper) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="menu"] > .site-builder-grid,
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="fullMenu"] > .site-builder-grid {
                background:
                    linear-gradient(90deg, transparent calc(50% - .5px), var(--cafe-rule) 50%, transparent calc(50% + .5px)),
                    var(--cafe-sheet);
                border: 1px solid var(--cafe-rule);
                box-shadow: 18px 20px 0 color-mix(in srgb, var(--cafe-forest) 10%, transparent);
                padding: clamp(2rem, 5vw, 5rem);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="menu"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) > .site-builder-column {
                background: transparent !important;
                border: 0 !important;
                border-bottom: 1px solid var(--cafe-rule) !important;
                border-radius: 0 !important;
                border-top: 1px solid var(--cafe-rule) !important;
                padding: 1rem 0 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="menu"], [data-web-region="fullMenu"]) .site-builder-card {
                background: transparent !important;
                border: 0 !important;
                border-bottom: 1px solid var(--cafe-rule) !important;
                border-radius: 0 !important;
                box-shadow: none !important;
                min-height: 132px;
                padding: 1.35rem 0 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="menu"], [data-web-region="fullMenu"]) .site-builder-card-content-inline {
                align-items: baseline !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="menu"], [data-web-region="fullMenu"]) .site-builder-card-content-inline > .site-builder-badge {
                background: transparent !important;
                border: 0 !important;
                border-radius: 0 !important;
                box-shadow: none !important;
                color: var(--cafe-copper-ink) !important;
                font-family: var(--site-heading-font) !important;
                font-size: 1.15rem !important;
                font-style: italic;
                font-weight: 600 !important;
                padding: 0 !important;
            }

            /* Roastery notes form a restrained ledger. */
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="experience"] {
                background: var(--cafe-forest) !important;
                border-bottom: 1px solid color-mix(in srgb, var(--cafe-sheet) 20%, transparent);
                border-top: 1px solid color-mix(in srgb, var(--cafe-sheet) 20%, transparent);
                color: var(--cafe-sheet) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="experience"] > .site-builder-grid {
                gap: 0 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item {
                border-right: 1px solid color-mix(in srgb, var(--cafe-sheet) 22%, transparent);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item:last-child {
                border-right: 0;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item > .site-builder-column {
                background: transparent !important;
                border-radius: 0 !important;
                min-height: 280px;
                padding: clamp(1.5rem, 3vw, 3rem) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="experience"] :where(.site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: var(--cafe-sheet) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item .site-builder-column > [data-site-builder-block="text"]:first-child .site-builder-text {
                color: color-mix(in srgb, var(--cafe-copper) 70%, var(--cafe-sheet)) !important;
                font-family: var(--site-heading-font) !important;
                font-size: clamp(3.5rem, 6vw, 6rem) !important;
                font-style: italic;
                font-weight: 400 !important;
                line-height: 1 !important;
            }

            /* A quiet editorial story section: image, copy and ruled notes. */
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="occasions"] {
                background: var(--cafe-sheet) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="occasions"] > .site-builder-grid > .site-builder-grid-item:first-child .site-builder-image {
                box-shadow: -22px 22px 0 var(--cafe-paper);
                filter: sepia(.1) saturate(.84);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="occasions"] .site-builder-card {
                background: transparent !important;
                border: 0 !important;
                border-radius: 0 !important;
                border-top: 1px solid var(--cafe-rule) !important;
                padding: 1.15rem 0 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="occasions"] .site-builder-card:last-child {
                border-bottom: 1px solid var(--cafe-rule) !important;
            }

            /* Reservation is the dark back page of the printed piece. */
            [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="reservation"], [data-web-region="reservationPage"]) {
                background: var(--cafe-coffee) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="reservation"], [data-web-region="reservationPage"]) > .site-builder-grid {
                gap: 0 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="reservation"], [data-web-region="reservationPage"]) > .site-builder-grid > .site-builder-grid-item > .site-builder-column {
                background: transparent !important;
                border: 1px solid color-mix(in srgb, var(--cafe-sheet) 20%, transparent) !important;
                border-radius: 0 !important;
                min-height: 100%;
                padding: clamp(1.75rem, 4vw, 4rem) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="reservation"], [data-web-region="reservationPage"]) :where(.site-builder-text, .site-builder-text > span, .site-builder-icon, label) {
                color: var(--cafe-sheet) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="reservation"], [data-web-region="reservationPage"]) :where(input, textarea, select) {
                border-bottom-color: color-mix(in srgb, var(--cafe-sheet) 32%, transparent) !important;
                color: var(--cafe-sheet) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="reservation"], [data-web-region="reservationPage"]) .site-builder-button {
                background: var(--cafe-copper) !important;
                border-color: var(--cafe-copper) !important;
                color: var(--cafe-sheet) !important;
            }

            /* Inner pages retain the publication system. */
            [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="contentHero"], [data-web-region="storyHero"]) {
                background-color: var(--cafe-paper) !important;
                border-bottom: 1px solid var(--cafe-rule);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="contentHero"] > .site-builder-grid > .site-builder-grid-item:first-child,
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="storyHero"] > .site-builder-grid > .site-builder-grid-item:first-child {
                border-left: 4px solid var(--cafe-copper);
                padding-left: clamp(1.25rem, 3vw, 3rem);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="storyHero"] .site-builder-image,
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="values"] .site-builder-image {
                box-shadow: 18px 18px 0 var(--cafe-paper);
                filter: sepia(.08) saturate(.9);
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="values"] .site-builder-card {
                background: transparent !important;
                border: 0 !important;
                border-left: 1px solid var(--cafe-rule) !important;
                border-radius: 0 !important;
                padding-left: clamp(1rem, 2vw, 2rem) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="footer"] {
                background: var(--cafe-forest) !important;
                border-top: 8px solid var(--cafe-copper) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="footer"] :where(.site-builder-text, .site-builder-text > span, .site-builder-icon) {
                color: var(--cafe-sheet) !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="footer"] [data-document-role="footerBrand"] > [data-site-builder-block="text"]:first-child .site-builder-text {
                font-family: var(--site-heading-font) !important;
                font-size: clamp(3.5rem, 7vw, 7rem) !important;
                font-style: italic;
                font-weight: 400 !important;
                letter-spacing: -.06em !important;
                line-height: .8 !important;
            }

            [data-web-template="restaurant-cafe-brunch"] [data-web-region="footer"] .site-builder-navigation-link:hover :where(.site-builder-text, .site-builder-text > span),
            [data-web-template="restaurant-cafe-brunch"] [data-web-region="footer"] a:hover :where(.site-builder-text, .site-builder-text > span) {
                color: color-mix(in srgb, var(--cafe-copper) 72%, var(--cafe-sheet)) !important;
            }

            @media (max-width: 900px) {
                [data-web-template="restaurant-cafe-brunch"] .site-builder-section {
                    padding-block: clamp(4rem, 16vw, 6rem);
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] {
                    min-height: 78px !important;
                    padding-inline: 1rem !important;
                    position: relative !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] > .site-builder-grid {
                    min-height: 78px;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    border-right: 0;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="brand"] {
                    border-right: 0;
                    padding-right: 0;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] [data-document-role="brandCopy"] > [data-site-builder-block="text"]:last-child {
                    display: none;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] .restaurant-mobile-menu-toggle {
                    border-color: var(--cafe-coffee) !important;
                    opacity: 1 !important;
                    pointer-events: auto !important;
                    transform: none !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"] .restaurant-mobile-menu-toggle > span {
                    background: var(--cafe-coffee) !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"][data-restaurant-mobile-menu-open] {
                    background: var(--cafe-paper) !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"][data-restaurant-mobile-menu-open] [data-document-role="headerNavigation"] {
                    border-top: 1px solid var(--cafe-rule) !important;
                    margin: 1rem 0 0;
                    padding-top: 1rem !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="header"][data-restaurant-mobile-menu-open] [data-document-role="headerActions"] {
                    border-left: 0;
                    padding-left: 0;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="hero"] {
                    background-position: center top !important;
                    background-size: 100% 45% !important;
                    min-height: 850px !important;
                    padding-bottom: 4rem !important;
                    padding-inline: 1rem !important;
                    padding-top: min(48vh, 390px) !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="hero"]::before {
                    background: linear-gradient(0deg, var(--cafe-paper) 0 49%, color-mix(in srgb, var(--cafe-paper) 94%, transparent) 60%, transparent 79%) !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="hero"]::after {
                    display: none;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="hero"] > .site-builder-grid {
                    align-items: end !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroHeading"] {
                    font-size: clamp(3.8rem, 18vw, 6rem) !important;
                    line-height: .82 !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-document-role="heroFeature"] {
                    display: none !important;
                }

                [data-web-template="restaurant-cafe-brunch"] :where(
                    [data-document-role="heroHighlightOne"],
                    [data-document-role="heroHighlightTwo"],
                    [data-document-role="heroHighlightThree"]
                ) {
                    width: 100%;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] {
                    background: color-mix(in srgb, var(--site-accent) 10%, var(--cafe-paper)) !important;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:first-child {
                    width: 100%;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:nth-child(2) .site-builder-image,
                [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:nth-child(3) .site-builder-image,
                [data-web-template="restaurant-cafe-brunch"] [data-web-region="gallery"] > .site-builder-grid > .site-builder-grid-item:nth-child(4) .site-builder-image {
                    transform: none;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="menu"] > .site-builder-grid,
                [data-web-template="restaurant-cafe-brunch"] [data-web-region="fullMenu"] > .site-builder-grid {
                    background: var(--cafe-sheet);
                    box-shadow: 8px 10px 0 color-mix(in srgb, var(--cafe-forest) 10%, transparent);
                    padding: 1.25rem;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item {
                    border-bottom: 1px solid color-mix(in srgb, var(--cafe-sheet) 22%, transparent);
                    border-right: 0;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item:last-child {
                    border-bottom: 0;
                }

                [data-web-template="restaurant-cafe-brunch"] [data-web-region="experience"] > .site-builder-grid > .site-builder-grid-item > .site-builder-column {
                    min-height: 0;
                }

                [data-web-template="restaurant-cafe-brunch"] :where([data-web-region="reservation"], [data-web-region="reservationPage"]) > .site-builder-grid > .site-builder-grid-item > .site-builder-column {
                    border-bottom: 0 !important;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="restaurant-cafe-brunch"] * {
                    scroll-behavior: auto !important;
                }
            }
        `}</style>
    );
}

export const restaurantCafeBrunchTemplate: SiteTemplateDefinition = {
    manifest: {
        ...restaurantModernTemplate.manifest,
        code: "restaurant-cafe-brunch",
        name: "Kavárna",
        description: "Světlá šablona pro kavárny s výběrovou kávou, pečivem a klidným posezením.",
        previewImage: "/templates/restaurant-cafe-brunch/preview.webp",
        defaults: {
            ...restaurantModernTemplate.manifest.defaults,
            colors: {
                primary: "#29463a",
                accent: "#97462d",
                background: "#f1e9da",
                surface: "#fbf7ee",
                text: "#241a15",
                muted: "#706157",
                border: "#c8b9a4",
            },
            typography: {
                heading: "Iowan Old Style, Baskerville, Times New Roman, serif",
                body: "Avenir Next, Inter, Helvetica Neue, Arial, sans-serif",
            },
            layout: {
                container: "1280px",
                sectionSpacing: "comfortable",
                radius: "2px",
            },
            components: {
                header: "builder",
                footer: "builder",
                buttons: "outline",
                forms: "line",
            },
        },
    },
    render: (props: TemplateRenderProps) => renderRestaurantTemplate(
        restaurantCafeBrunchTemplate,
        props,
        <CafeStyles />,
    ),
};
